import React, { useMemo, useState, useEffect } from "react";
import { DashboardLayout } from "./Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import { ccmPatientsHeaders } from "@/utils/tablesData";
import { useNavigate } from "react-router";
import {
  Calendar,
  Eye,
  History,
  Pencil,
  PlusCircle,
  Repeat,
  Stethoscope,
  User,
} from "lucide-react";
import { useOrg } from "@/contexts/useOrg";
import { useAuth } from "@/contexts/use-auth";
import AxiosInstance from "@/utils/AxiosInstance";
import { toast } from "sonner";
import {
  formatDate,
  getCurrentMonthYear,
  getErrToast,
  getPromise,
  getPromiseToast,
} from "@/utils/helpers";
import {
  extractTcmPlans,
  extractTcmPlansTotal,
  labelTcmValue,
} from "@/components/patient-v2/tcm/tcmApi";
import { ToggleGroup } from "@radix-ui/react-toggle-group";
import { ToggleGroupItem } from "@/components/ui/toggle-group";
import { useGetData } from "@/hooks/useGetData";
import { Clock, Edit3, Tag, Heart, FileText } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import MyDialogContent from "@/components/MyDialogContent";
import NursingNoteTablePopup from "@/components/NursingNoteTablePopup";

const TCM_PLAN_HEADERS = [
  { id: "patientId", label: "ID", isSortable: false },
  { id: "patientName", label: "Patient", isSortable: false },
  { id: "dischargeDate", label: "Discharge Date", isSortable: false },
  { id: "dischargedFrom", label: "Discharged From", isSortable: false },
  { id: "dischargedTo", label: "Discharged To", isSortable: false },
  { id: "complexity", label: "Complexity", isSortable: false },
  { id: "planStatus", label: "Plan Status", isSortable: false },
  { id: "telehealthStatus", label: "Telehealth", isSortable: false },
  { id: "createdAt", label: "Created At", isSortable: false },
  { id: "createdBy", label: "Created By", isSortable: false },
];

const TCM_PLAN_FILTERS = [
  {
    label: "Month",
    key: "date",
    type: "monthYear" as const,
  },
  {
    label: "Patient Type",
    key: "patientType",
    isMulti: false,
    values: [
      { label: "Outpatient", key: "outpatient" },
      { label: "Facility", key: "facility" },
    ],
  },
  {
    label: "Patient Status",
    key: "patientStatus",
    isMulti: false,
    values: [
      { label: "Active", key: "active" },
      { label: "Inactive", key: "inactive" },
      { label: "Newly Added", key: "newly_added" },
    ],
  },
  {
    label: "Plan Status",
    key: "status",
    isMulti: false,
    values: [
      { label: "In Progress", key: "in_progress" },
      { label: "Non Compliant", key: "non_compliant" },
      { label: "Complete", key: "complete" },
    ],
  },
];

const CCMPatients = () => {
  const [ccmPatients, setCcmPatients] = useState([]);
  const { selectedOrgId, isAllSelected } = useOrg();
  const { user, profile: profileFromAuth, isLoading: authLoading } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [filters, setFilters] = useState<Record<string, any>>({
    date: getCurrentMonthYear(),
  });
  const [totalCCm, setTotalCCM] = useState(0);
  const [totalPcm, setTotalPCM] = useState(0);
  const [totalRpm, setTotalRPM] = useState(0);
  const [totalApcm, setTotalAPCM] = useState(0);
  const [totalTcm, setTotalTCM] = useState(0);
  const [nursingNotes, setNursingNotes] = useState<
    { patientId: string; program?: string } | false
  >(false);
  const [nursingLoading, setNursingLoading] = useState(false);
  const [facilities, setFacilities] = useState<any[]>([]);

  useGetData(
    "/patients/ccm",
    { elig: true, orgId: selectedOrgId },
    false,
    true,
    (err, data) => {
      if (!err && data) {
        setTotalCCM(data?.data?.total);
      }
    },
  );
  useGetData(
    "/patients/pcm",
    { elig: true, orgId: selectedOrgId },
    false,
    true,
    (err, data) => {
      if (!err && data) {
        setTotalPCM(data?.data?.total);
      }
    },
  );
  useGetData(
    "/patients/apcm",
    { elig: true, orgId: selectedOrgId },
    false,
    true,
    (err, data) => {
      if (!err && data) {
        setTotalAPCM(data?.data?.total);
      }
    },
  );
  useGetData(
    "/tcm-plans",
    {
      date: getCurrentMonthYear(),
      page: 1,
      limit: 20,
      ...(!isAllSelected && selectedOrgId && selectedOrgId !== "anthro-care"
        ? { organizationId : selectedOrgId }
        : {}),
    },
    false,
    true,
    (err, data) => {
      if (!err && data) {
        setTotalTCM(extractTcmPlansTotal(data) ?? extractTcmPlans(data).length);
      }
    },
  );
  useGetData(
    "/patients/rpm",
    { elig: true, orgId: selectedOrgId },
    false,
    true,
    (err, data) => {
      if (!err && data) {
        setTotalRPM(data?.data?.total);
      }
    },
  );
  const nav = useNavigate();
  const [patientsView, setPatientsView] = useState("ccm");
  const IS_CCM = patientsView === "ccm";
  const IS_PCM = patientsView === "pcm";
  const IS_APCM = patientsView === "apcm";
  const IS_TCM = patientsView === "tcm";
  const IS_RPM = patientsView === "rpm";

  const hasSelectedOrg =
    !!selectedOrgId && !isAllSelected && selectedOrgId !== "anthro-care";

  useGetData(
    "/facilities",
    hasSelectedOrg ? { orgId: selectedOrgId, limit: 500 } : { limit: 500 },
    false,
    !!user && IS_APCM,
    (err, data) => {
      if (!err && data?.data?.facilities) {
        setFacilities(data.data.facilities);
      } else if (err) {
        setFacilities([]);
      }
    },
  );

  useEffect(() => {
    setFacilities([]);
  }, [selectedOrgId]);

  const isProviderOrPractitioner =
    user?.role?.toLowerCase() === "provider" ||
    user?.role?.toLowerCase() === "practitioner";
  const isProviderOrPractitionerManager =
    user?.role === "manager" &&
    (user?.managerScope === "provider" ||
      user?.managerScope === "practitioner");
  const isCaretaker = user?.role === "caretaker";

  // Fetch profile if not available from auth context
  useEffect(() => {
    if (!authLoading && user) {
      if (profileFromAuth) {
        setProfile(profileFromAuth);
      } else if (isCaretaker) {
        const fetchProfile = async () => {
          try {
            const response = await AxiosInstance().get<any>("/auth/me");
            const responseData = response?.data as any;
            if (responseData?.data?.profile) {
              setProfile(responseData.data.profile);
            }
          } catch (error) {
            console.error("Error fetching profile:", error);
          }
        };
        fetchProfile();
      }
    }
  }, [user, profileFromAuth, authLoading, isCaretaker]);

  // Check if caretaker is assigned to the patient
  const isCaretakerAssignedToPatient = (rowData: any) => {
    if (!isCaretaker) return true;
    const patient =
      ccmPatients.find((p: any) => p._id === rowData?._id) || rowData;
    const assignedCaretakerIds = (patient?.assignedCaretakerIds || []).map(
      (v: any) => (typeof v === "string" ? v : v?._id),
    );
    const profileId = profile?._id;
    if (!profileId) return false;
    return assignedCaretakerIds.includes(profileId);
  };

  // Dynamic column headers based on selected tab
  const dynamicHeaders = useMemo(() => {
    return ccmPatientsHeaders
      .filter((header) => {
        // Hide currentMonthMinutes column
        if (header.id === "currentMonthMinutes") {
          return false;
        }
        // Show only the relevant minutes column for each tab
        if (header.id === "ccmMinutes") {
          return IS_CCM;
        }
        if (header.id === "pcmMinutes") {
          return IS_PCM;
        }
        if (
          header.id === "rpmMinutes" ||
          header.id === "lastTreatmentPlanStatus"
        ) {
          return IS_RPM;
        }
        if (header.id === "readingCount") {
          return IS_RPM;
        }
        return true;
      })
      .map((header) => {
        // Replace hasCCMCarePlan with hasPCMCarePlan for PCM tab or hasRPMCarePlan for RPM tab
        if (header.id === "hasCCMCarePlan" && !IS_CCM) {
          return {
            ...header,
            id: IS_PCM
              ? "hasPCMCarePlan"
              : IS_APCM
                ? "hasAPCMCarePlan"
                : IS_TCM
                  ? "hasTCMCarePlan"
                  : "hasRPMCarePlan",
          };
        }
        if (header.id === "createdBy") {
          return {
            ...header,
            label: IS_CCM
              ? "CCM Created By"
              : IS_PCM
                ? "PCM Created By"
                : IS_APCM
                  ? "APCM Created By"
                  : IS_TCM
                    ? "TCM Created By"
                    : "RPM Created By",
          };
        }
        if (header.id === "createdAt") {
          return {
            ...header,
            label: IS_CCM
              ? "CCM Created Date"
              : IS_PCM
                ? "PCM Created Date"
                : IS_APCM
                  ? "APCM Created Date"
                  : IS_TCM
                    ? "TCM Created Date"
                    : "RPM Created Date",
            sortKey: IS_CCM
              ? "lastCCMCarePlan"
              : IS_PCM
                ? "lastPCMCarePlan"
                : IS_APCM
                  ? "lastAPCMCarePlan"
                  : IS_TCM
                    ? "lastTCMCarePlan"
                    : "lastTreatmentPlan",
          };
        }
        return header;
      });
  }, [IS_CCM, IS_PCM, IS_APCM, IS_TCM, IS_RPM]);

  const apcmHeaders = useMemo(() => {
    const facilityHeader = {
      id: "facility",
      label: "Facility",
      isSortable: false,
    };
    const nameIndex = dynamicHeaders.findIndex(
      (header) => header.id === "firstName",
    );
    if (nameIndex === -1) return [...dynamicHeaders, facilityHeader];
    const headers = [...dynamicHeaders];
    headers.splice(nameIndex + 1, 0, facilityHeader);
    return headers;
  }, [dynamicHeaders]);

  const filterOptions = useMemo(() => {
    return [
      {
        label: "Month",
        key: "date",
        type: "monthYear" as const,
      },
      {
        label: "Plan Status",
        key: "carePlan",
        isMulti: false,
        values: [
          { label: "Pending", key: "pending" },
          { label: "Completed", key: "added" },
          // { label: "Added Plan", key: "" },
          ...(!IS_RPM && !IS_APCM && !IS_TCM ? [{ label: "Pending Follow up", key: "followUp" }] : []),
          //   { label: "Facing issue", key: "facing-issue" },
          //   { label: "Inactive", key: "inactive" },
        ],
      },
      {
        label: "Patient Type",
        key: "patientType",
        isMulti: false,
        values: [
          { label: "Outpatient", key: "outpatient" },
          { label: "Facility", key: "facility" },
        ],
      },
      {
        label: "Patient Status",
        key: "status",
        isMulti: false,
        values: [
          { label: "Active", key: "active" },
          { label: "Inactive", key: "inactive" },
          { label: "Newly Added", key: "newly_added" },
        ],
      },
      ...(IS_APCM
        ? [
            {
              label: "Facility",
              key: "facilityIds",
              isMulti: true,
              values: facilities.map((facility: any) => ({
                label: facility?.name || "Unnamed facility",
                key: facility?._id,
              })),
            },
          ]
        : []),
    ];
  }, [IS_RPM, IS_APCM, IS_TCM, facilities]);

  async function getCarePlanData(
    pData: any,
    cb: any,
    planType?: "pcm" | "rpm" | "apcm" | "tcm",
  ) {
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      const endpoint =
        planType === "pcm"
          ? "pcm-careplan"
          : planType === "rpm"
            ? "rpm-careplan"
            : planType === "apcm"
              ? "careplan"
              : planType === "tcm"
                ? "tcm-careplan"
                : "careplan";
      const { data }: any = await AxiosInstance().get(
        `/${endpoint}?patientId=${pData?._id}`,
      );
      if (!data?.data?.carePlan?.[0]?._id) {
        throw new Error("No careplan found");
      }
      data?.data?.carePlan?.[0]?._id ? cb(data?.data?.carePlan?.[0]) : "";
      resolveFn("Careplan Fetched Successfully");
    } catch (error) {
      rejectFn(error);
    }
  }

  async function getTreatmentPlanData(
    pData: any,
    treatmentPlanId: string,
    cb: any,
  ) {
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      const { data }: any = await AxiosInstance().get(
        `/treatment-plan/${treatmentPlanId}`,
      );
      if (!data?.data?.treatmentPlan?._id) {
        throw new Error("No treatment plan found");
      }
      data?.data?.treatmentPlan?._id ? cb(data?.data?.treatmentPlan) : "";
      resolveFn("Treatment Plan Fetched Successfully");
    } catch (error) {
      rejectFn(error);
    }
  }

  return (
    <DashboardLayout activePage="/ccm-patients">
      <div className="mb-4 w-full border-none rounded-md">
        <ToggleGroup
          type="single"
          value={patientsView}
          onValueChange={(value) => {
            if (value) setPatientsView(value);
            setCcmPatients([]);
            setFilters({ date: getCurrentMonthYear() });
          }}
          className="mb-4 flex w-full overflow-x-auto rounded-lg"
        >
          <ToggleGroupItem
            value="ccm"
            aria-label="ccm"
            className={`px-4 py-2 text-sm font-medium flex-none teachers ${patientsView === "ccm" ? "bg-primary text-white" : ""}`}
            style={{
              backgroundColor: patientsView === "ccm" ? "#3463AA" : "#EEEEEE",
            }}
          >
            <User /> CCM ({totalCCm})
          </ToggleGroupItem>
          <ToggleGroupItem
            value="pcm"
            aria-label="pcm"
            className={`px-4 py-2 text-sm font-medium flex-none teachers ${patientsView === "pcm" ? "bg-primary text-white" : ""}`}
            style={{
              backgroundColor: patientsView === "pcm" ? "#3463AA" : "#EEEEEE",
            }}
          >
            <User /> PCM ({totalPcm})
          </ToggleGroupItem>
          <ToggleGroupItem
            value="apcm"
            aria-label="apcm"
            className={`px-4 py-2 text-sm font-medium flex-none teachers ${patientsView === "apcm" ? "bg-primary text-white" : ""}`}
            style={{
              backgroundColor: patientsView === "apcm" ? "#3463AA" : "#EEEEEE",
            }}
          >
            <User /> APCM ({totalApcm})
          </ToggleGroupItem>
          <ToggleGroupItem
            value="tcm"
            aria-label="tcm"
            className={`px-4 py-2 text-sm font-medium flex-none teachers ${patientsView === "tcm" ? "bg-primary text-white" : ""}`}
            style={{
              backgroundColor: patientsView === "tcm" ? "#3463AA" : "#EEEEEE",
            }}
          >
            <User /> TCM ({totalTcm})
          </ToggleGroupItem>
          <ToggleGroupItem
            value="rpm"
            aria-label="rpm"
            className={`px-4 py-2 text-sm font-medium flex-none teachers ${patientsView === "rpm" ? "bg-primary text-white" : ""}`}
            style={{
              backgroundColor: patientsView === "rpm" ? "#3463AA" : "#EEEEEE",
            }}
          >
            <User /> RPM ({totalRpm})
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      {IS_CCM && (
        <TableWithSearch
          key={"ccm"}
          columnPreferenceKey={`ccm-program-patients-${patientsView}`}
          universalLoadingSkeleton
          fetchCondition={true}
          heading="CCM Patients List"
          isIncludeSearch
          apiDataKey="patients"
          columnHeaders={dynamicHeaders}
          searchPlaceholder={`Search ${patientsView} patients`}
          url={"/patients/ccm"}
          params={{ elig: true, orgId: selectedOrgId }}
          data={ccmPatients}
          viewData={ccmPatients.map((v: any) => {
            const caretakerMinutes = v?.caretakerMinutes || {};
            const toDateOnly = (val: any) =>
              val ? new Date(val).toISOString().split("T")[0] : "";
            const createdDate =
              toDateOnly(v?.lastCCMCarePlan) || toDateOnly(v?.lastCarePlan);
            return {
              ...v,
              firstName: `${v?.firstName || ""} ${v?.middleName || ""} ${v?.lastName || ""}`,
              createdAt: createdDate,
              createdBy: v?.lastCCMCarePlanCreatedByName || "-",
              ccmMinutes: caretakerMinutes?.ccmCurrentMonthMinutes || 0,
              ...caretakerMinutes,
              lastCarePlanStatus:
                v?.lastCarePlanStatus === "followUp"
                  ? "pending followUp"
                  : v?.lastCarePlanStatus,
            };
          })}
          setData={setCcmPatients}
          isActionColumn={false && !isProviderOrPractitioner}
          handleAction={(data) => {
            if (isProviderOrPractitioner || isProviderOrPractitionerManager)
              return;
            if (isCaretaker && !isCaretakerAssignedToPatient(data)) {
              nav("/patients/" + data?._id);
            } else {
              nav("/patients/" + data?._id + "/edit", { state: { data } });
            }
          }}
          isActionDisabled={(data) =>
            isCaretaker && !isCaretakerAssignedToPatient(data)
          }
          // clickRow={(data:any)=> nav(data?._id,{state: {data}})}
          isColumnSelection
          filters={filters}
          clickRow={() => {}}
          filtersOptions={filterOptions}
          setFilters={setFilters}
          // isDownloadData
          moreActions={
            isProviderOrPractitioner || isProviderOrPractitionerManager
              ? [
                  {
                    label: "View",
                    icon: <Eye size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(data, (cData: any) => {
                        nav(`/patient-careplan/${cData?._id}`, {
                          state: { carePlan: cData },
                        });
                      });
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" ||
                      data?.lastCarePlanStatus == "followUp",
                    // isDisabled: (data: any) => isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  //  {
                  //   label: "Edit Care Plan",
                  //   icon: <Pencil size={16} />,
                  //   onClick: (data: any) => {
                  //     getCarePlanData(data, (cData: any) => {
                  //       nav(`/careplan/${data?._id}/${cData?._id}`, {
                  //         state: { carePlan: cData },
                  //       });
                  //     });
                  //   },
                  //   isShow: (data: any) =>
                  //     data?.lastCarePlanStatus == "added",
                  //   isDisabled: (data: any) =>
                  //     isCaretaker && !isCaretakerAssignedToPatient(data),
                  // },
                ]
              : [
                  {
                    label: "Edit Care Plan",
                    icon: <Pencil size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(data, (cData: any) => {
                        nav(`/careplan/${data?._id}/${cData?._id}`, {
                          state: { carePlan: cData },
                        });
                      });
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" &&
                      user?.role !== "provider",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "View",
                    icon: <Eye size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(data, (cData: any) => {
                        nav(`/patient-careplan/${cData?._id}`, {
                          state: { carePlan: cData },
                        });
                      });
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" ||
                      data?.lastCarePlanStatus == "followUp",
                    // isDisabled: (data: any) => isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "Add",
                    icon: <PlusCircle size={16} />,
                    onClick: (data: any) => {
                      nav(`/careplan/${data?._id}`);
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "pending",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "History",
                    icon: <History size={16} />,
                    onClick: (data: any) => {
                      nav(`/patients/${data?._id}?tab=care-plan`);
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" || true,
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "Create Follow up",
                    icon: <Repeat size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(data, (cData: any) => {
                        nav(
                          `/careplan/${data?._id}/${cData?._id}?type=followup`,
                          {
                            state: { carePlan: cData },
                          },
                        );
                      });
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "followUp",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                ]
          }
          clickCell={[
            {
              headerKey: "currentMonthMinutes",
              isClickable: (data: any) => {
                console.log(data);
                return !!data?.currentMonthMinutes;
              },
              onClick: async (formdata: any) => {
                console.log(formdata);
                // try {
                setNursingLoading(true);
                setNursingNotes({ patientId: formdata?._id, program: "ccm" });
                //   const { data }: any = await AxiosInstance().get(
                //     "/patients/nursing-note",
                //     {
                //       params: {
                //         patientId: formdata?._id,
                //         date: getCurrentMonthYear(),
                //       },
                //     },
                //   );
                //   setNursingNotes(data?.data?.notes);
                //   setNursingLoading(false);
                // } catch (error) {
                //   setNursingLoading(false);
                //   console.log(error);
                // }
              },
            },
            {
              headerKey: "ccmCurrentMonthMinutes",
              isClickable: (data: any) => {
                return !!data?.ccmCurrentMonthMinutes;
              },
              onClick: async (formdata: any) => {
                setNursingLoading(true);
                setNursingNotes({ patientId: formdata?._id, program: "ccm" });
              },
            },
          ]}
        />
      )}
      {IS_PCM && (
        <TableWithSearch
          key={"pcm"}
          columnPreferenceKey={`pcm-program-patients-${patientsView}`}
          universalLoadingSkeleton
          fetchCondition={true}
          heading="PCM Patients List"
          isIncludeSearch
          apiDataKey="patients"
          columnHeaders={dynamicHeaders}
          searchPlaceholder={`Search ${patientsView} patients`}
          url={"/patients/pcm"}
          params={{ elig: true, orgId: selectedOrgId }}
          data={ccmPatients}
          viewData={ccmPatients.map((v: any) => {
            const caretakerMinutes = v?.caretakerMinutes || {};
            const toDateOnly = (val: any) =>
              val ? new Date(val).toISOString().split("T")[0] : "";
            const createdDate =
              toDateOnly(v?.lastPCMCarePlan) || toDateOnly(v?.lastCarePlan);
            return {
              ...v,
              firstName: `${v?.firstName || ""} ${v?.middleName || ""} ${v?.lastName || ""}`,
              createdAt: createdDate,
              createdBy: v?.lastPCMCarePlanCreatedByName || "-",
              pcmMinutes: caretakerMinutes?.pcmCurrentMonthMinutes || 0,
              ...caretakerMinutes,
              lastCarePlanStatus:
                v?.lastCarePlanStatus === "followUp"
                  ? "pending followUp"
                  : v?.lastCarePlanStatus,
            };
          })}
          setData={setCcmPatients}
          isActionColumn={false && !isProviderOrPractitioner}
          handleAction={(data) => {
            if (isProviderOrPractitioner || isProviderOrPractitionerManager)
              return;
            if (isCaretaker && !isCaretakerAssignedToPatient(data)) {
              nav("/patients/" + data?._id);
            } else {
              nav("/patients/" + data?._id + "/edit", { state: { data } });
            }
          }}
          isActionDisabled={(data) =>
            isCaretaker && !isCaretakerAssignedToPatient(data)
          }
          // clickRow={(data:any)=> nav(data?._id,{state: {data}})}
          isColumnSelection
          filters={filters}
          clickRow={() => {}}
          filtersOptions={filterOptions}
          setFilters={setFilters}
          // isDownloadData
          moreActions={
            isProviderOrPractitioner || isProviderOrPractitionerManager
              ? [
                  {
                    label: "View",
                    icon: <Eye size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(`/patient-careplan/${cData?._id}?plan=pcm`, {
                            state: { carePlan: cData },
                          });
                        },
                        "pcm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" ||
                      data?.lastCarePlanStatus == "followUp",
                    // isDisabled: (data: any) => isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "History",
                    icon: <History size={16} />,
                    onClick: (data: any) => {
                      nav(`/patients/${data?._id}?tab=care-plan&plan=pcm`);
                    },
                    isShow: () => true,
                  },
                  // {
                  //   label: "Edit Care Plan",
                  //   icon: <Pencil size={16} />,

                  //   onClick: (data: any) => {
                  //     getCarePlanData(
                  //       data,
                  //       (cData: any) => {
                  //         nav(`/careplan/${data?._id}/${cData?._id}?plan=pcm`, {
                  //           state: { carePlan: cData },
                  //         });
                  //       },
                  //       "pcm",
                  //     );
                  //   },
                  //   isShow: (data: any) =>
                  //     data?.lastCarePlanStatus == "added",
                  //   isDisabled: (data: any) =>
                  //     isCaretaker && !isCaretakerAssignedToPatient(data),
                  // },
                ]
              : [
                  {
                    label: "Edit Care Plan",
                    icon: <Pencil size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(`/careplan/${data?._id}/${cData?._id}?plan=pcm`, {
                            state: { carePlan: cData },
                          });
                        },
                        "pcm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" &&
                      user?.role !== "provider",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "View",
                    icon: <Eye size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(`/patient-careplan/${cData?._id}?plan=pcm`, {
                            state: { carePlan: cData },
                          });
                        },
                        "pcm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" ||
                      data?.lastCarePlanStatus == "followUp",
                    // isDisabled: (data: any) => isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "Add",
                    icon: <PlusCircle size={16} />,
                    onClick: (data: any) => {
                      nav(`/careplan/${data?._id}?plan=pcm`);
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "pending",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "History",
                    icon: <History size={16} />,
                    onClick: (data: any) => {
                      nav(`/patients/${data?._id}?tab=care-plan&plan=pcm`);
                    },
                    isShow: () => true,
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "Create Follow up",
                    icon: <Repeat size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(
                            `/careplan/${data?._id}/${cData?._id}?type=followup&plan=pcm`,
                            {
                              state: { carePlan: cData },
                            },
                          );
                        },
                        "pcm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "followUp",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                ]
          }
          clickCell={[
            {
              headerKey: "currentMonthMinutes",
              isClickable: (data: any) => {
                console.log(data);
                return !!data?.currentMonthMinutes;
              },
              onClick: async (formdata: any) => {
                console.log(formdata);
                setNursingNotes({ patientId: formdata?._id, program: "pcm" });
              },
            },
            {
              headerKey: "pcmCurrentMonthMinutes",
              isClickable: (data: any) => {
                return !!data?.pcmCurrentMonthMinutes;
              },
              onClick: async (formdata: any) => {
                setNursingLoading(true);
                setNursingNotes({ patientId: formdata?._id, program: "pcm" });
              },
            },
          ]}
        />
      )}
      {IS_APCM && (
        <TableWithSearch
          key={"apcm"}
          columnPreferenceKey={`apcm-program-patients-${patientsView}`}
          universalLoadingSkeleton
          fetchCondition={true}
          heading="APCM Patients List"
          isIncludeSearch
          apiDataKey="patients"
          columnHeaders={apcmHeaders}
          searchPlaceholder={`Search ${patientsView} patients`}
          url={"/patients/apcm"}
          params={{ elig: true, orgId: selectedOrgId }}
          onFetchError={(err) => getErrToast(err)}
          data={ccmPatients}
          viewData={ccmPatients.map((v: any) => {
            const toDateOnly = (val: any) =>
              val ? new Date(val).toISOString().split("T")[0] : "";
            const createdDate =
              toDateOnly(v?.lastAPCMCarePlan) || toDateOnly(v?.lastCarePlan);
            const facilityRef = v?.facilityId ?? v?.facility;
            const facilityName =
              typeof facilityRef === "object" && facilityRef?.name
                ? facilityRef.name
                : typeof v?.facilityName === "string" && v.facilityName
                  ? v.facilityName
                  : "-";
            return {
              ...v,
              firstName: `${v?.firstName || ""} ${v?.middleName || ""} ${v?.lastName || ""}`,
              facility: facilityName,
              createdAt: createdDate,
              createdBy: v?.lastAPCMCarePlanCreatedByName || "-",
              lastCarePlanStatus:
                v?.lastCarePlanStatus === "followUp"
                  ? "pending followUp"
                  : v?.lastCarePlanStatus,
            };
          })}
          setData={setCcmPatients}
          isActionColumn={false && !isProviderOrPractitioner}
          handleAction={(data) => {
            if (isProviderOrPractitioner || isProviderOrPractitionerManager)
              return;
            if (isCaretaker && !isCaretakerAssignedToPatient(data)) {
              nav("/patients/" + data?._id);
            } else {
              nav("/patients/" + data?._id + "/edit", { state: { data } });
            }
          }}
          isActionDisabled={(data) =>
            isCaretaker && !isCaretakerAssignedToPatient(data)
          }
          isColumnSelection
          filters={filters}
          clickRow={() => {}}
          filtersOptions={filterOptions}
          setFilters={setFilters}
          moreActions={
            isProviderOrPractitioner || isProviderOrPractitionerManager
              ? [
                  {
                    label: "View",
                    icon: <Eye size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(`/patient-careplan/${cData?._id}?plan=apcm`, {
                            state: { carePlan: cData },
                          });
                        },
                        "apcm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" ||
                      data?.lastCarePlanStatus == "followUp",
                  },
                  {
                    label: "History",
                    icon: <History size={16} />,
                    onClick: (data: any) => {
                      nav(`/patients/${data?._id}?tab=care-plan&plan=apcm`);
                    },
                    isShow: () => true,
                  },
                ]
              : [
                  {
                    label: "Edit Care Plan",
                    icon: <Pencil size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(`/careplan/${data?._id}/${cData?._id}?plan=apcm`, {
                            state: { carePlan: cData },
                          });
                        },
                        "apcm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" &&
                      user?.role !== "provider",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "View",
                    icon: <Eye size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(`/patient-careplan/${cData?._id}?plan=apcm`, {
                            state: { carePlan: cData },
                          });
                        },
                        "apcm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "added" ||
                      data?.lastCarePlanStatus == "followUp",
                  },
                  {
                    label: "Add",
                    icon: <PlusCircle size={16} />,
                    onClick: (data: any) => {
                      nav(`/careplan/${data?._id}?plan=apcm`);
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "pending",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "History",
                    icon: <History size={16} />,
                    onClick: (data: any) => {
                      nav(`/patients/${data?._id}?tab=care-plan&plan=apcm`);
                    },
                    isShow: () => true,
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "Create Follow up",
                    icon: <Repeat size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(
                            `/careplan/${data?._id}/${cData?._id}?type=followup&plan=apcm`,
                            {
                              state: { carePlan: cData },
                            },
                          );
                        },
                        "apcm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "followUp",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                ]
          }
        />
      )}
      {IS_TCM && (
        <TableWithSearch
          key={"tcm"}
          columnPreferenceKey={`tcm-program-plans-${patientsView}`}
          universalLoadingSkeleton
          fetchCondition={true}
          heading="TCM Plans"
          isIncludeSearch
          apiDataKey="plans"
          columnHeaders={TCM_PLAN_HEADERS}
          searchPlaceholder="Search TCM Patients"
          url="/tcm-plans"
          params={
            hasSelectedOrg ? { orgId: selectedOrgId } : {}
          }
          customFetch={async ({ page, limit, search, filters: tableFilters, params: tableParams }) => {
            const requestParams: Record<string, string | number> = {
              page,
              limit,
            };
            if (tableFilters?.date) requestParams.date = tableFilters.date;
            if (tableFilters?.status) requestParams.status = tableFilters.status;
            if (tableFilters?.patientType) requestParams.patientType = tableFilters.patientType;
            if (tableFilters?.patientStatus) requestParams.patientStatus = tableFilters.patientStatus;
            if (search) requestParams.search = search;
            if (tableParams?.orgId) requestParams.orgId = tableParams.orgId;
            try {
              const { data } = await AxiosInstance().get("/tcm-plans", {
                params: requestParams,
              });
              const rows = extractTcmPlans(data).map((plan) => ({
                ...plan,
                _id: plan.patientId,
                patientId: plan.patientCode || "—",
                patientName: plan.patientName || "—",
                dischargeDate: formatDate(plan.dischargeDate) || "—",
                dischargedFrom:
                  [labelTcmValue(plan.dischargedFrom)] // , plan.dischargeFacility
                  .filter(Boolean)
                    .join(" - ") || "—",
                dischargedTo: labelTcmValue(plan.dischargedTo) || "—",
                complexity: labelTcmValue(plan.complexity) || "—",
                planStatus: labelTcmValue(plan.planStatus) || "—",
                telehealthStatus: labelTcmValue(plan.telehealthStatus) || "—",
                createdAt: formatDate(plan.createdAt) || "—",
                createdBy: plan.createdBy || "—",
              }));
              return {
                data: rows,
                total: extractTcmPlansTotal(data) ?? rows.length,
              };
            } catch (error) {
              getErrToast(error);
              throw error;
            }
          }}
          data={ccmPatients}
          viewData={ccmPatients}
          setData={setCcmPatients}
          isActionColumn={false}
          handleAction={() => {}}
          isColumnSelection
          filters={filters}
          clickRow={() => {}}
          filtersOptions={TCM_PLAN_FILTERS}
          setFilters={setFilters}
          moreActions={[
            {
              label: "Edit TCM plan",
              icon: <Pencil size={16} />,
              onClick: (data: any) => {
                if (!data?._id || !data?.id) return;
                nav(`/tcm-plan/${data._id}/${data.id}`);
              },
              isShow: (data: any) =>
                !!data?._id &&
                !!data?.id &&
                !isProviderOrPractitioner &&
                !isProviderOrPractitionerManager,
            },
            {
              label: "History",
              icon: <History size={16} />,
              onClick: (data: any) => {
                if (!data?._id) return;
                nav(`/patients/${data._id}?tab=care-plan&subtab=tcm-plan`);
              },
              isShow: (data: any) => !!data?._id,
            },
          ]}
        />
      )}
      {IS_RPM && (
        <TableWithSearch
          key={"rpm"}
          columnPreferenceKey={`rpm-program-patients-${patientsView}`}
          universalLoadingSkeleton
          fetchCondition={true}
          heading="RPM Program Patients List"
          isIncludeSearch
          apiDataKey="patients"
          columnHeaders={dynamicHeaders}
          searchPlaceholder={`Search ${patientsView} patients`}
          url={"/patients/rpm"}
          params={{ elig: true, orgId: selectedOrgId }}
          data={ccmPatients}
          viewData={ccmPatients.map((v: any) => {
            const caretakerMinutes = v?.caretakerMinutes || {};
            const toDateOnly = (val: any) =>
              val ? new Date(val).toISOString().split("T")[0] : "";
            const createdDate = toDateOnly(v?.lastTreatmentPlan);
            return {
              ...v,
              firstName: `${v?.firstName || ""} ${v?.middleName || ""} ${v?.lastName || ""}`,
              createdAt: createdDate,
              createdBy: v?.lastTreatmentPlanCreatedByName || "-",
              email: v?.email || "",
              primaryNumber: v?.primaryNumber || "",
              lastVisit: v?.lastVisit || "",
              rpmMinutes: caretakerMinutes?.rpmCurrentMonthMinutes || 0,
              ...caretakerMinutes,
              hasRPMCarePlan: v?.hasRPMCarePlan || "no",
              // lastCarePlanStatus: v?.lastCarePlanStatus || "",
              lastCarePlanStatus:
                v?.lastCarePlanStatus === "followUp"
                  ? "pending followUp"
                  : v?.lastCarePlanStatus,
              typeOfPatient: v?.typeOfPatient || "",
              status: v?.status || "",
            };
          })}
          setData={setCcmPatients}
          isActionColumn={false && !isProviderOrPractitioner}
          handleAction={(data) => {
            if (isProviderOrPractitioner || isProviderOrPractitionerManager)
              return;
            if (isCaretaker && !isCaretakerAssignedToPatient(data)) {
              nav("/patients/" + data?._id);
            } else {
              nav("/patients/" + data?._id + "/edit", { state: { data } });
            }
          }}
          isActionDisabled={(data) =>
            isCaretaker && !isCaretakerAssignedToPatient(data)
          }
          // clickRow={(data:any)=> nav(data?._id,{state: {data}})}
          isColumnSelection
          filters={filters}
          clickRow={() => {}}
          filtersOptions={filterOptions}
          setFilters={setFilters}
          // isDownloadData
          moreActions={
            isProviderOrPractitioner || isProviderOrPractitionerManager
              ? [
                  {
                    label: "View",
                    icon: <Eye size={16} />,
                    onClick: (data: any) => {
                      nav(
                        `/treatment-plan/${data?._id}/${data?.lastTreatmentPlanId}?view=true`,
                      );
                    },
                    isShow: (data: any) =>
                      !!(data?._id && data?.lastTreatmentPlanId) &&
                      (data?.lastTreatmentPlanStatus == "added" ||
                        data?.lastCarePlanStatus == "followUp"),
                    // isDisabled: (data: any) => isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "History",
                    icon: <History size={16} />,
                    onClick: (data: any) => {
                      nav(`/patients/${data?._id}?tab=treatment-plan`);
                    },
                    isShow: () => true,
                  },
                  // {
                  //   label: "Edit Treatment Plan",
                  //   icon: <Pencil size={16} />,
                  //   onClick: (data: any) => {
                  //     nav(
                  //       `/treatment-plan/${data?._id}/${data?.lastTreatmentPlanId}`,
                  //     );
                  //   },
                  //   isShow: (data: any) =>
                  //     data?.lastTreatmentPlanStatus == "added",

                  //   isDisabled: (data: any) =>
                  //     isCaretaker && !isCaretakerAssignedToPatient(data),
                  // },
                ]
              : [
                  {
                    label: "Edit Treatment Plan",
                    icon: <Pencil size={16} />,
                    onClick: (data: any) => {
                      nav(
                        `/treatment-plan/${data?._id}/${data?.lastTreatmentPlanId}`,
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastTreatmentPlanStatus == "added" &&
                      user?.role !== "provider",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "View",
                    icon: <Eye size={16} />,
                    onClick: (data: any) => {
                      nav(
                        `/treatment-plan/${data?._id}/${data?.lastTreatmentPlanId}?view=true`,
                      );
                    },
                    isShow: (data: any) =>
                      !!(data?._id && data?.lastTreatmentPlanId) &&
                      (data?.lastTreatmentPlanStatus == "added" ||
                        data?.lastCarePlanStatus == "followUp"),
                    // isDisabled: (data: any) => isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "Add",
                    icon: <PlusCircle size={16} />,
                    onClick: (data: any) => {
                      nav(`/treatment-plan/${data?._id}`);
                    },
                    isShow: (data: any) =>
                      data?.lastTreatmentPlanStatus == "pending",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "History",
                    icon: <History size={16} />,
                    onClick: (data: any) => {
                      nav(`/patients/${data?._id}?tab=treatment-plan`);
                    },
                    isShow: () => true,
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                  {
                    label: "Create Follow up",
                    icon: <Repeat size={16} />,
                    onClick: (data: any) => {
                      getCarePlanData(
                        data,
                        (cData: any) => {
                          nav(
                            `/careplan/${data?._id}/${cData?._id}?type=followup&plan=rpm`,
                            {
                              state: { carePlan: cData },
                            },
                          );
                        },
                        "rpm",
                      );
                    },
                    isShow: (data: any) =>
                      data?.lastCarePlanStatus == "followUp",
                    isDisabled: (data: any) =>
                      isCaretaker && !isCaretakerAssignedToPatient(data),
                  },
                ]
          }
          clickCell={[
            {
              headerKey: "currentMonthMinutes",
              isClickable: (data: any) => {
                console.log(data);
                return !!data?.currentMonthMinutes;
              },
              onClick: async (formdata: any) => {
                console.log(formdata);
                setNursingNotes({ patientId: formdata?._id, program: "rpm" });
              },
            },
            {
              headerKey: "rpmCurrentMonthMinutes",
              isClickable: (data: any) => {
                return !!data?.rpmCurrentMonthMinutes;
              },
              onClick: async (formdata: any) => {
                setNursingLoading(true);
                setNursingNotes({ patientId: formdata?._id, program: "rpm" });
              },
            },
          ]}
        />
      )}
      <Dialog
        open={!!nursingNotes}
        onOpenChange={(o) => !o && setNursingNotes(false)}
      >
        <MyDialogContent heading="Nursing Notes">
          <NursingNoteTablePopup
            patientId={
              typeof nursingNotes === "object"
                ? nursingNotes.patientId
                : nursingNotes
            }
            program={
              typeof nursingNotes === "object"
                ? nursingNotes.program
                : undefined
            }
          />
        </MyDialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CCMPatients;
